// src/pages/ExternalResources.tsx
import React from "react";
import { Helmet } from "@dr.pogodin/react-helmet";

export default function ExternalResources() {
  const canonical = "https://free1099calc.com/blog/external";

  const links = [
    {
      title: "IRS — Self-Employed Individuals Tax Center",
      url: "https://www.irs.gov/businesses/small-businesses-self-employed/self-employed-individuals-tax-center",
      desc: "Official IRS hub for 1099 and small-business tax topics."
    },
    {
      title: "SSA — Retirement Benefits",
      url: "https://www.ssa.gov/benefits/retirement/",
      desc: "Eligibility, estimates, and application for Social Security retirement."
    },
    {
      title: "BLS — Consumer Price Index (CPI) Overview",
      url: "https://www.bls.gov/cpi/",
      desc: "U.S. inflation methodology, latest CPI releases, and historical data."
    },
    {
      title: "FINRA — Fees & Investment Costs",
      url: "https://www.finra.org/investors/learn-to-invest/advanced-investing/fees",
      desc: "Understand expense ratios, trading costs, and how fees impact returns."
    }
  ];

  return (
    <main className="mx-auto max-w-6xl px-4 md:px-6 py-8">
      <Helmet>
        <title>Authoritative Tax & Financial Resources - IRS, SSA, BLS, FINRA | Free1099Calc</title>
        <meta
          name="description"
          content="Curated authoritative links to IRS self-employment resources, Social Security Administration retirement guides, BLS inflation data, and FINRA investment cost information."
        />
        <meta name="keywords" content="IRS resources, SSA retirement, BLS CPI data, FINRA investment costs, tax resources, authoritative financial information, government tax guides" />
        <link rel="canonical" href={canonical} />
        
        {/* Open Graph tags */}
        <meta property="og:title" content="Authoritative Tax & Financial Resources" />
        <meta property="og:description" content="Official IRS, SSA, BLS, and FINRA resources for taxes, retirement, inflation, and investing." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://free1099calc.com/blog/external" />
        
        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Tax & Financial Resources - IRS, SSA, BLS, FINRA" />
        <meta name="twitter:description" content="Authoritative government resources for 1099 taxes, retirement, and financial planning." />
      </Helmet>

      <section className="mb-8 rounded-2xl border bg-white p-6 md:p-8">
        <h1 className="text-3xl md:text-4xl font-bold">External Resources</h1>
        <p className="mt-3 text-slate-700">
          Curated authoritative references for deeper reading and verification.
        </p>
      </section>

      <section className="grid gap-4 sm:grid-cols-2">
        {links.map((l) => (
          <a
            key={l.url}
            href={l.url}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-2xl border bg-white p-5 hover:bg-slate-50 transition"
          >
            <h3 className="font-semibold">{l.title}</h3>
            <p className="mt-1 text-sm text-slate-600">{l.desc}</p>
            <p className="mt-1 text-xs text-slate-500 break-all">{l.url}</p>
          </a>
        ))}
      </section>

      {/* Educational Content */}
      <section className="mt-10 rounded-2xl border bg-white p-6 md:p-8">
        <h2 className="text-2xl font-bold mb-4">Why These Resources Matter for 1099 Workers</h2>
        
        <div className="space-y-6 text-slate-700">
          <div>
            <h3 className="text-xl font-semibold mb-3">Navigating Authoritative Tax Information</h3>
            <p className="mb-4">
              As a <strong>freelancer, independent contractor, or self-employed professional</strong>, you're responsible 
              for understanding and complying with tax laws that traditional employees never need to think about. While 
              there's no shortage of tax advice online, the quality and accuracy varies dramatically. The resources listed 
              above are <strong>authoritative primary sources</strong>—the official government agencies that write and 
              enforce the rules.
            </p>
            <p>
              When you have a tax question, always verify information against these official sources rather than relying 
              solely on blogs, forums, or AI-generated content (including this site). Tax laws change annually, and only 
              the IRS and state tax agencies publish definitive guidance.
            </p>
          </div>

          <div>
            <h3 className="text-xl font-semibold mb-3">IRS Self-Employed Tax Center</h3>
            <p className="mb-4">
              The <strong>IRS Self-Employed Individuals Tax Center</strong> is your go-to resource for understanding 1099 
              taxes, self-employment tax, quarterly estimated payments, and business deductions. Key topics covered include:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li><strong>Form 1040 Schedule C:</strong> How to report profit or loss from your business</li>
              <li><strong>Form 1040 Schedule SE:</strong> Calculating self-employment tax (Social Security and Medicare)</li>
              <li><strong>Form 1040-ES:</strong> Quarterly estimated tax payment vouchers and worksheets</li>
              <li><strong>Business expense deductions:</strong> What you can deduct, how to document it, and common mistakes</li>
              <li><strong>Home office deduction:</strong> Simplified vs. actual expense method</li>
              <li><strong>Mileage and vehicle expenses:</strong> Standard mileage rate vs. actual expense method</li>
            </ul>
            <p>
              The IRS also publishes <strong>Publication 334 (Tax Guide for Small Business)</strong> and <strong>Publication 
              535 (Business Expenses)</strong>, which provide comprehensive guidance on deductions and recordkeeping 
              requirements.
            </p>
          </div>

          <div>
            <h3 className="text-xl font-semibold mb-3">Social Security Administration Retirement Benefits</h3>
            <p className="mb-4">
              As a 1099 worker, you're still building Social Security credits and Medicare eligibility through self-employment 
              tax payments. The <strong>SSA Retirement Benefits</strong> portal helps you:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li><strong>Check your earnings record:</strong> Create a my Social Security account to verify your lifetime earnings and projected benefits</li>
              <li><strong>Understand benefit calculations:</strong> How your 35 highest-earning years determine your monthly benefit</li>
              <li><strong>Plan claiming strategy:</strong> Early retirement at 62 vs. full retirement age (67 for most) vs. delayed retirement credits until 70</li>
              <li><strong>Coordinate with spouse:</strong> Spousal benefits, survivor benefits, and strategies for married couples</li>
              <li><strong>Work in retirement:</strong> Earnings limits and how working affects benefits before full retirement age</li>
            </ul>
            <p>
              <strong>Important for freelancers:</strong> You need at least 40 credits (typically 10 years of work) to qualify 
              for Social Security retirement benefits. If you had W-2 jobs earlier in your career, those credits count toward 
              this requirement.
            </p>
          </div>

          <div>
            <h3 className="text-xl font-semibold mb-3">Bureau of Labor Statistics CPI Data</h3>
            <p className="mb-4">
              The <strong>Bureau of Labor Statistics (BLS)</strong> publishes the Consumer Price Index (CPI), the most 
              widely used measure of U.S. inflation. For freelancers, understanding inflation is critical for:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li><strong>Rate adjustments:</strong> Knowing annual inflation helps you justify rate increases to clients</li>
              <li><strong>Long-term contracts:</strong> Include CPI-based escalation clauses in multi-year agreements</li>
              <li><strong>Retirement planning:</strong> Convert future dollar needs to today's purchasing power</li>
              <li><strong>Business budgeting:</strong> Anticipate cost increases for software, insurance, and equipment</li>
            </ul>
            <p>
              The BLS website provides historical CPI data, regional breakdowns, and methodology explanations. Pay attention 
              to both <strong>headline CPI</strong> (includes food and energy) and <strong>core CPI</strong> (excludes 
              volatile food and energy prices) when tracking long-term trends.
            </p>
          </div>

          <div>
            <h3 className="text-xl font-semibold mb-3">FINRA Investment Cost Information</h3>
            <p className="mb-4">
              The <strong>Financial Industry Regulatory Authority (FINRA)</strong> educates investors about fees and costs 
              that eat into investment returns. As a self-employed professional managing your own retirement accounts, 
              understanding investment costs is crucial:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li><strong>Expense ratios:</strong> Annual fees charged by mutual funds and ETFs, typically 0.03-1.5%</li>
              <li><strong>Advisory fees:</strong> What robo-advisors and financial advisors charge (0.25-1% annually)</li>
              <li><strong>Trading costs:</strong> Commissions, bid-ask spreads, and transaction fees</li>
              <li><strong>Load fees:</strong> Front-end or back-end sales charges on some mutual funds (avoid these)</li>
              <li><strong>12b-1 fees:</strong> Marketing fees embedded in some fund expense ratios</li>
            </ul>
            <p className="mb-4">
              <strong>Example impact:</strong> A 1% annual fee on a $500,000 retirement portfolio costs $5,000 per year. 
              Over 30 years with 7% returns, that 1% fee difference costs approximately $379,000 in lost compound growth.
            </p>
            <p>
              FINRA's tools help you compare funds, understand prospectus disclosures, and calculate the real impact of fees 
              on long-term wealth building.
            </p>
          </div>

          <div>
            <h3 className="text-xl font-semibold mb-3">How to Use These Resources Effectively</h3>
            <ol className="list-decimal pl-6 space-y-2 mb-4">
              <li><strong>Start with the basics:</strong> Read IRS Publication 334 cover-to-cover when you first start freelancing</li>
              <li><strong>Bookmark key pages:</strong> Save links to estimated tax payment forms, mileage rates, and retirement benefit calculators</li>
              <li><strong>Check for updates annually:</strong> Tax brackets, standard deductions, and contribution limits change each year</li>
              <li><strong>Verify third-party advice:</strong> When a blog or accountant gives you advice, cross-reference it with IRS publications</li>
              <li><strong>Use official calculators:</strong> The SSA retirement estimator and IRS withholding calculator are more accurate than third-party tools</li>
              <li><strong>Read the instructions:</strong> IRS form instructions often contain valuable tax planning guidance</li>
            </ol>
          </div>

          <div>
            <h3 className="text-xl font-semibold mb-3">State and Local Resources</h3>
            <p className="mb-4">
              While the resources above cover federal taxes and benefits, don't forget <strong>state and local requirements</strong>:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li><strong>State tax agencies:</strong> Your state's Department of Revenue or Taxation website for state income tax rules</li>
              <li><strong>City/county business licenses:</strong> Many jurisdictions require business licenses or permits for self-employment</li>
              <li><strong>Sales tax:</strong> If you sell products or certain services, you may need to collect and remit sales tax</li>
              <li><strong>Unemployment insurance:</strong> Some states require self-employed individuals to pay into unemployment systems</li>
            </ul>
            <p>
              Search "[Your State] self-employment tax" and "[Your City] business license" to find relevant local resources.
            </p>
          </div>

          <div className="bg-blue-50 border-l-4 border-primary-600 p-4 rounded">
            <p className="text-sm text-slate-800">
              <strong>Disclaimer:</strong> While these government resources are authoritative, tax and financial situations 
              vary widely. Always consult with a qualified CPA, Enrolled Agent (EA), or financial advisor for personalized 
              guidance. This page provides educational information only and does not constitute professional tax, legal, or 
              financial advice.
            </p>
          </div>
        </div>
      </section>
    </main>
  );
}
